import React, { useMemo } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import { CreateTableNew as CreateTable } from "../../../../../common/APICommon";
import { StatusBadge } from "../../../../../common/Badge";
import { formatDate, formatDateTime } from "../../../../../utils/textUtils";
import { FEATURES } from "../../../../../config/features";
import { REQUEST_TYPES, WorkflowStatus } from "../../../../../utils/enums";
import { getRblLevelStatus } from "../utils/transactionRequestUtils";

export function TransactionRequestTable({
  data,
  requestType,
  onView,
  onApprove,
  onDisapprove,
  currentPage,
  setCurrentPage,
  setConfirmationConfig
}) {

  const tableConfig = useMemo(() => {
    switch (requestType) {

      case 1: // Leave
        return {
          headers: ["Ref No.", "Employee", "Date From", "Date To", "Request Date", "Status", "Actions"],
          accessors: ["formattedReferenceNumber", "fullName", "dateFrom", "dateTo", "requestDate", "status", "actions"]
        };

      case 2: // Change Schedule
        return {
          headers: ["Ref No.", "Employee", "Date From", "Date To", "Request Date", "Status", "Actions"],
          accessors: ["formattedReferenceNumber", "fullName", "dateFrom", "dateTo", "requestDate", "status", "actions"]
        };
      case 3: // Overtime
        return {
          headers: ["Ref No.", "Employee", "OT Date", "From", "To", "Hours", "Request Date", "Status", "Actions"],
          accessors: ["formattedReferenceNumber", "fullName", "otDate", "requestDateTimeFrom", "requestDateTimeTo", "otHours", "requestDate", "status", "actions"]
        };
      case 4: // DTR Correction
        return {
          headers: ["Ref No.", "Employee", "Log Date", "Type", "Request Date", "Status", "Actions"],
          accessors: ["formattedReferenceNumber", "fullName", "otDate", "changeLogType", "requestDate", "status", "actions"]
        };
      case 5: // Offset
        return {
          headers: ["Ref No.", "Employee", "Offset Type", "Ref Date", "Request Date", "Status", "Actions"],
          accessors: ["formattedReferenceNumber", "fullName", "offsetType", "dateFrom", "requestDate", "status", "actions"]
        };
      case 6: // Leave Recall
        return {
          headers: ["Ref No.", "Employee", "Leave Recall Request", "Request Date", "Status", "Actions"],
          accessors: ["formattedReferenceNumber", "fullName", "requestTypeDescription", "requestDate", "status", "actions"]
        };
      default: // ALL
        return {
          headers: ["Ref No.", "Employee", "Type", "Request Date", "Status", "Actions"],
          accessors: ["formattedReferenceNumber", "fullName", "requestTypeDescription", "requestDate", "status", "actions"]
        };
    }
  }, [requestType]);

  const actions = [
    {
      name: "Approve",
      icon: <ThumbUpIcon fontSize="small" />,
      className: "bg-blue-600 hover:bg-blue-700",
      function: (item) => {
        if (FEATURES.APPROVER_HIERARCHY_RBL_SPECIFIC_CONFIRMATION_LABEL) {
          if (item.hierarchy === 1) {
            setConfirmationConfig({
              title: "Confirm Recommendation",
              actionText: "recommend"
            });
          } else if (item.hierarchy === 2) {
            setConfirmationConfig({
              title: "Confirm Approval",
              actionText: "approve"
            });
          } else {
            setConfirmationConfig({
              title: "Confirm Approval",
              actionText: "approve"
            });
          }
        } else {
          setConfirmationConfig({
            title: "Confirm Approval",
            actionText: "approve"
          });
        }

        onApprove(item);
      },
      visible: (item) => item.isPendingMyAction === true,
    },
    {
      name: "Disapprove",
      icon: <ThumbDownAltIcon fontSize="small" />,
      className: "bg-red-500 hover:bg-red-600",
      function: (item) => onDisapprove(item),
      visible: (item) => item.isPendingMyAction === true,
    },
    {
      name: "View",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-cyan-500 hover:bg-cyan-600",
      function: (item) => onView(item),
      visible: true,
    }
  ];

  return (
    <CreateTable
      headers={tableConfig.headers}
      accessors={tableConfig.accessors}
      data={data}
      actions={actions}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      itemsPerPage={10}
      totalItems={data?.length || 0}
      customCellRenderer={(item, acc) => {
        if (["requestDate", "dateFrom", "dateTo", "otDate"].includes(acc)) return <span>{formatDate(item[acc])}</span>;
        if (["requestDateTimeFrom", "requestDateTimeTo"].includes(acc)) return <span>{formatDateTime(item[acc])}</span>;
        if (acc === "status") {
          const updatedStatus = FEATURES.APPROVER_HIERARCHY_RBL_SPECIFIC_CONFIRMATION_LABEL ? getRblLevelStatus({item, requestTypeId: item?.requestTypeId}) : item[acc];
          return <StatusBadge status={updatedStatus} />;
        }
        if (acc === "formattedReferenceNumber") return <b className="text-blue-700">{item[acc]}</b>;
        if (acc === "otHours") return <span className="font-mono">{Number(item[acc] || 0).toFixed(2)}</span>;
        return null;
      }}
    />
  );
}